import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ErrorPage from "../pages/ErrorPage";
import api from "../api";
import ReactMarkdown from "react-markdown";
import Modal from "../components/Modal";

export default function DetailsAnaliseJuridica() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [comentarioRejeicao, setComentarioRejeicao] = useState("");

  useEffect(() => {
    const fetchLog = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/AI/${id}`);
        console.log(res.data);
        setLog(res.data);
        setError(null);
      } catch {
        setError("Erro ao carregar os detalhes.");
      } finally {
        setLoading(false);
      }
    };

    fetchLog();
  }, [id]);

  if (error) {
    return <ErrorPage message={error} onBack={() => navigate(-1)} />;
  }

  if (!log) {
    return (
      <ErrorPage message="Log não encontrado." onBack={() => navigate(-1)} />
    );
  }

  const handleAprovar = async () => {
    toast.success("Análise jurídica aprovada com sucesso!");
    toast.warning("Análise financeira em processamento!");

    navigate("/precatorios");

    try {
      await api.put(`/AI/aprovaAnaliseJuridica/${id}`).then(() => {
        toast.success("Análise financeira disponível para consulta.");
      });
    } catch {
      toast.error("Erro ao aprovar a análise jurídica.");
    }
  };

  const handleRejeitar = () => setShowModal(true);

  const handleRejeitarConfirmado = async () => {
    if (!comentarioRejeicao.trim()) {
      toast.warning("Por favor, insira o motivo da rejeição.");
      return;
    }

    try {
      await api.put(`/AI/rejeitaAnaliseJuridica/${id}`, {
        comment: comentarioRejeicao.trim(),
      });

      toast.success("Análise jurídica rejeitado com sucesso!");
      toast.warning("Análise financeira não será processada.");

      setShowModal(false);
      setComentarioRejeicao("");
    } catch {
      toast.error("Erro ao rejeitar o log.");
    }

    window.location.reload();
  };

  if (loading)
    return <p className="text-center text-lg mt-10">Carregando...</p>;

  if (error)
    return (
      <div className="text-center mt-10">
        <p>{error}</p>
        <button onClick={() => navigate(-1)} className="button button-azul">
          Voltar
        </button>
      </div>
    );

  if (!log)
    return (
      <div className="text-center mt-10">
        <p>Log não encontrado.</p>
        <button onClick={() => navigate(-1)} className="button button-azul">
          Voltar
        </button>
      </div>
    );

  let cleanedJson = log.response;
  if (typeof cleanedJson === "string") {
    cleanedJson = cleanedJson.replace(/```json|```/g, "").trim();
  }
  let data = {};
  try {
    data = JSON.parse(cleanedJson);
  } catch (e) {
    console.error("Erro ao fazer parse do JSON:", e);
    data = {};
  }

  const Section = ({ title, children }) => (
    <div className="bg-gray-50 rounded-xl shadow-md p-4 mb-6">
      <h3 className="text-xl font-bold mb-2 text-blue-800">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );

  const KeyValue = ({ label, value }) => (
    <p>
      <span style={{ fontWeight: "bold" }}>{label}:</span>{" "}
      <span className="text-gray-700">{value}</span>
    </p>
  );

  if (log.response === null) {
    return (
      <ErrorPage
        message={`Erro ao carregar. Precatório com Status: ${log.status}`}
        onBack={() => navigate(-1)}
      />
    );
  } else {
    return (
      <div className="max-w-6xl mx-auto mt-10 px-4 text-sm md:text-base">
        <div
          style={{
            display: "flex",
            gap: "20px",
            minHeight: "600px",
          }}
        >
          {/* Coluna esquerda: PDF */}
          <div style={{ flex: 1, minWidth: "50%", minHeight: "600px" }}>
            {log.fileUrls ? (
              <iframe
                src={log.fileUrls}
                title="Visualizador de PDF"
                width="100%"
                height="100%"
                style={{ border: "none" }}
              />
            ) : (
              <p style={{ padding: "1rem", textAlign: "center" }}>
                Nenhum arquivo PDF disponível para visualização.
              </p>
            )}
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-center mb-6">
              {log.stage} - {log.status}
            </h2>

            {log.status === "Rejeitado" && (
              <Section
                title={`Rejeitado por ${log.user.email} em ${new Date(
                  log.updatedAt
                ).toLocaleString()}`}
              >
                <KeyValue label="Motivo" value={log.comment} />
              </Section>
            )}

            <Section title="Informações Básicas">
              <KeyValue label="Nome do Arquivo" value={log.nameFile} />
              <KeyValue label="Upload feito por" value={log.user.email} />
              <KeyValue
                label="Upload em"
                value={new Date(log.createdAt).toLocaleString()}
              />
              <KeyValue
                label="Atualizado em"
                value={new Date(log.updatedAt).toLocaleString()}
              />
              <KeyValue
                label="Processo Principal"
                value={data?.numeroProcessoPrincipal}
              />
              <KeyValue
                label="Processo Precatório"
                value={data?.numeroProcessoPrecatorio}
              />
              <KeyValue
                label="Trânsito em Julgado"
                value={data?.dataTransitoEmJulgado}
              />
              <KeyValue label="Juízo" value={data?.juizo} />
              <KeyValue label="Credor" value={data?.credor} />
              <KeyValue label="Devedor" value={data?.devedor} />
              <KeyValue
                label="Valor Global"
                value={`R$ ${
                  data?.valorGlobalRequisitado?.toLocaleString() || "N/A"
                }`}
              />
            </Section>

            <Section title="Composição do Valor Requisitado">
              {Object.entries(data?.composicaoValorRequisitado || {}).map(
                ([k, v]) =>
                  k !== "explicacoes" && (
                    <KeyValue
                      key={k}
                      label={k}
                      value={`R$ ${v?.toLocaleString?.() || v || "N/A"}`}
                    />
                  )
              )}
              {data?.composicaoValorRequisitado?.explicacoes && (
                <ReactMarkdown>
                  {data?.composicaoValorRequisitado.explicacoes}
                </ReactMarkdown>
              )}
            </Section>

            {(data?.certidoes || []).length > 0 && (
              <Section title="Certidões">
                {(data?.certidoes || []).map((certidao, i) => (
                  <KeyValue
                    key={i}
                    label={certidao.tipo}
                    value={certidao.status}
                  />
                ))}
              </Section>
            )}

            {(data?.processosCorrelatos || []).length > 0 && (
              <Section title="Processos Correlatos">
                {(data?.processosCorrelatos || []).map((proc, i) => (
                  <li key={i}>{proc}</li>
                ))}
              </Section>
            )}

            {(data?.alertasRiscos || []).length > 0 && (
              <Section title="Alertas e Riscos">
                {(data?.alertasRiscos || []).map((alerta, i) => (
                  <li key={i} className="text-orange-600">
                    {alerta}
                  </li>
                ))}
              </Section>
            )}

            <Section title="Preferência Legal">
              <KeyValue
                label="Idade do Credor"
                value={data?.preferenciaLegal?.idadeCredor || "Não informado"}
              />
              <KeyValue
                label="Preferência Legal (60+)"
                value={
                  data?.preferenciaLegal?.direitoPreferenciaLegal
                    ? "Sim"
                    : "Não"
                }
              />
              {data?.preferenciaLegal?.explicacoes && (
                <ReactMarkdown>
                  {data?.preferenciaLegal.explicacoes}
                </ReactMarkdown>
              )}
            </Section>

            <Section title="Análise Semântica">
              {Object.entries(data?.analiseSemantica || {}).map(
                ([chave, item]) => (
                  <div key={chave} className="mb-2">
                    <KeyValue
                      label={chave}
                      value={`(fator de risco: ${item.fatorRisco})`}
                    />
                    <ReactMarkdown>
                      {`Explicações: ${item.explicacoes}`}
                    </ReactMarkdown>
                  </div>
                )
              )}
            </Section>

            <Section title="Validação">
              <KeyValue
                label="Status da Validação"
                value={(data?.validacao).toUpperCase()}
              />
              <ReactMarkdown>{data?.motivoValidacao}</ReactMarkdown>
            </Section>

            {data?.resumoOficio && (
              <Section title="Resumo do Ofício">
                <ReactMarkdown>{data?.resumoOficio}</ReactMarkdown>
              </Section>
            )}

            {data?.outrasInformacoes && (
              <Section title="Outras Informações">
                <ReactMarkdown>{data?.outrasInformacoes}</ReactMarkdown>
              </Section>
            )}
          </div>
        </div>
        {log.status === "Pendente" && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "1rem",
              marginTop: "2rem",
            }}
          >
            <button
              onClick={() => navigate(-1)}
              style={{
                height: "100%",
                margin: "0 10px",
              }}
              className="button button-azul"
            >
              Voltar
            </button>

            <button
              onClick={() => handleAprovar()}
              style={{
                height: "100%",
                margin: "0 10px",
              }}
              className="button button-verde"
            >
              Aprovar
            </button>
            <button
              onClick={() => handleRejeitar()}
              style={{
                height: "100%",
                margin: "0 10px",
              }}
              className="button button-vermelho"
            >
              Rejeitar
            </button>
          </div>
        )}

        {log.status === "Rejeitado" && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "1rem",
              marginTop: "2rem",
            }}
          >
            <button
              onClick={() => navigate(-1)}
              style={{
                height: "100%",
                margin: "0 10px",
              }}
              className="button button-azul"
            >
              Voltar
            </button>
          </div>
        )}

        {showModal ? (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0,0,0,0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <Modal
              isOpen={showModal}
              onClose={() => setShowModal(false)}
              onConfirm={handleRejeitarConfirmado}
              title="Rejeitar Análise Jurídica"
              confirmText="Rejeitar"
              cancelText="Voltar"
            >
              <p className="mb-2 text-gray-700">Insira o motivo da rejeição:</p>
              <textarea
                style={{
                  width: " 100%",
                  minHeight: " 120px",
                  padding: " 0.75rem 1rem",
                  border: " 1px solid #ccc",
                  borderRadius: " 8px",
                  fontSize: " 1rem",
                  resize: " vertical",
                  fontFamily: " inherit",
                  boxSizing: " border-box",
                  transition: " border-color 0.3s ease",
                }}
                value={comentarioRejeicao}
                onChange={(e) => setComentarioRejeicao(e.target.value)}
                rows={6}
                className="w-full border border-gray-300 rounded-md p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                placeholder="Digite o motivo da rejeição..."
              />
            </Modal>
          </div>
        ) : null}
      </div>
    );
  }
}
